import { EVENT_NAME as SYNC_STATE_CHANGE_EVENT } from '@adobe-fonts/site/events/SyncStateChangeEvent.js';

import { handleFirstMileFontActivation } from '../dialogs/FontActivationFirstMileDialog.ts';
import { shouldShowRemoveFamilyConfirmationDialog, showRemoveFamilyConfirmationDialog } from '../dialogs/RemoveFamilyConfirmationDialog.ts';

import { handleAuthenticationWithPostLoginAction } from '../../util/authentication_util.js';
import { addPageAction } from '../../util/new_relic.js';
import { dispatchRetirementNotificationAlert } from '../../util/notification_helpers.ts';
import { getNotificationService } from '../../util/notification_service.ts';
import { getData } from '../../util/script_data.js';
import { syncVariations, unsyncVariations } from '../../util/Sync.js';

const notificationService = getNotificationService();

export function registerEventHandlers(element) {
  element.addEventListener(
    SYNC_STATE_CHANGE_EVENT, handleSyncStateChangeEvent);
}

function handleSyncStateChangeEvent(event) {
  const card = event.target.closest('af-family-card');
  if (!card) {
    return;
  }

  const syncButton = card.querySelector('af-sync-button');
  const isSynced = syncButton ? syncButton.hasAttribute('synced') : card.hasAttribute('synced');

  if (isSynced) {
    addPageAction('typekit.click.unsync-family-button', {
      family: card.dataset.familyName
    });

    unsyncFamilyForCard(card);
  } else {
    addPageAction('typekit.click.sync-family-button', {
      family: card.dataset.familyName
    });

    syncFamilyForCard(card);
  }
}

async function syncFamilyForCard(card) {
  const ids = getUnsyncedIds(card);
  const slug = card.dataset.slug;
  if (!ids || !slug) {
    return;
  }

  try {
    await handleAuthenticationWithPostLoginAction('activate_family', {
      slug
    });

    const response = await syncVariations(ids);
    const responseData = await response.json();
    setSynced(card, responseData.added_font_ids);
    showSyncSuccessMessage(card);
  } catch (e) {
    showSyncErrorMessage(card);
  }
}

function showSyncSuccessMessage(card) {
  handleFirstMileFontActivation({
    queryParams: {
      familyName: card.dataset.familyName
    }
  });
}

function showSyncErrorMessage(card) {
  const i18n = getData('/neue/i18n');
  notificationService.error(
    i18n['neue.browse.family_card']['error_occurred_adding_some_fonts'], {
      actionLabel: i18n['neue.browse.family_card']['try_again'],
      callbackAction: () => syncFamilyForCard(card)
    });
}

async function unsyncFamilyForCard(card) {
  const ids = getSyncedIds(card);
  if (!ids) {
    return;
  }

  await confirmFamilyUsync();
  try {
    const response = await unsyncVariations(ids);
    const responseData = await response.json();
    setUnsynced(card, responseData.removed_font_ids);
    showUnsyncSuccessMessage();
  } catch (e) {
    showUnsyncErrorMessage();
  }
}

async function confirmFamilyUsync() {
  return new Promise(resolve => {
    shouldShowRemoveFamilyConfirmationDialog().then(shouldShow => {
      if (shouldShow) {
        const i18n = getData('/neue/i18n');
        const header = i18n['neue.browse.family_card']['remove_family_confirmation_header'];
        const description = i18n['neue.browse.family_card']['remove_family_confirmation_description'];
        showRemoveFamilyConfirmationDialog(header, description, resolve);
      } else {
        resolve();
      }
    });
  });
}

function showUnsyncSuccessMessage() {
  const i18n = getData('/neue/i18n');
  notificationService.notice(i18n['neue.browse.family_card']['fonts_removed_success_message']);
}

function showUnsyncErrorMessage() {
  const i18n = getData('/neue/i18n');
  notificationService.error(
    i18n['neue.browse.family_card']['error_occurred_deactivating_fonts']);
}

function setSynced(card, ids) {
  card.dataset.syncedIds = JSON.stringify(ids);
  delete card.dataset.unsyncedIds;

  const syncButton = card.querySelector('af-sync-button');
  if (syncButton) {
    syncButton.toggleAttribute('synced', true);
  } else {
    card.toggleAttribute('synced', true);
  }

  dispatchRetirementNotificationAlert('family', card.dataset.slug, card.dataset.familyName, true);
}

function setUnsynced(card, ids) {
  card.dataset.unsyncedIds = JSON.stringify(ids);
  delete card.dataset.syncedIds;

  const syncButton = card.querySelector('af-sync-button');
  if (syncButton) {
    syncButton.toggleAttribute('synced', false);
  } else {
    card.toggleAttribute('synced', false);
  }

  dispatchRetirementNotificationAlert('family', card.dataset.slug, card.dataset.familyName, false);
}

function getUnsyncedIds(card) {
  if (!('unsyncedIds' in card.dataset)) {
    return;
  }

  try {
    const unsyncedIds = JSON.parse(card.dataset.unsyncedIds);
    if (unsyncedIds && unsyncedIds.length > 0) {
      return unsyncedIds;
    }
  } catch (e) {
    return [];
  }
}

function getSyncedIds(card) {
  if (!('syncedIds' in card.dataset)) {
    return;
  }

  try {
    const syncedIds = JSON.parse(card.dataset.syncedIds);
    if (syncedIds && syncedIds.length > 0) {
      return syncedIds;
    }
  } catch (e) {
    return [];
  }
}
